import type { ComponentProps } from "react"
import { useFormState } from "react-hook-form"
import { AutocompleteChips } from "@motleyhand/design"
import FormControl from "./FormControl"
import getOwnMessage from "./getOwnMessage"

type Props = Omit<
  ComponentProps<typeof AutocompleteChips>,
  "onChange" | "value"
> & {
  name: string
  required?: boolean
}

export default function FormAutocompleteChips({
  name,
  required,
  ...props
}: Props) {
  // Unscoped: RHF's name filter misses the API's bracketed paths
  // (`tools[0].allowedTools[1]` doesn't start with `tools.0.allowedTools`).
  const { errors } = useFormState()

  return (
    <FormControl name={name} required={required}>
      {({ field: { value, onChange }, isError, error }) => {
        // FormControl hands a stored null over as "".
        const chips = Array.isArray(value)
          ? value.filter((chip): chip is string => typeof chip === "string")
          : []
        // A violation on one chip (`tags[0]`) lands under `tags.0`, which the
        // list's own message never sees.
        const chipError = chips
          .map((chip, i) => {
            const message = getOwnMessage(errors, `${name}.${i}`)

            return message && `${chip}: ${message}`
          })
          .find(Boolean)

        return (
          <AutocompleteChips
            value={chips}
            onChange={onChange}
            isError={isError || !!chipError}
            error={error ?? chipError}
            {...props}
          />
        )
      }}
    </FormControl>
  )
}
